import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import {
    HashRouter as Router,
    Switch,
    Route,
    Redirect,
    useLocation,
} from 'react-router-dom';
import { Location as HistoryLocation } from 'history';
import { shallowEqual, useDispatch } from 'react-redux';
import { loadTheme } from '@fluentui/react';
import {
    resolveFluentTokens,
    buildNeutralPalette,
} from '@sendent/nextcloud-api';
import queryString from 'query-string';
import { useAppSelector } from './store';
import { logger } from './helpers/logger';
import { appActions } from './store/actions/app/actions';
import { uiActions } from './store/actions/ui/actions';
import { Footer, Waiting, NotificationBar } from './components/common';
import { WhatsNewDialog } from './components/dialogs';
import { useReleaseNotes } from './helpers/hooks';
import { selectIsFetching } from './store/selectors/app';
import { selectUser } from './store/selectors/auth';
import { UserStorageHelper } from './helpers';
import { BODY_FONT_STACK, resolveThemeColors } from './helpers/theme';
import {
    LoginPage,
    SettingsLayout,
    NextcloudExplorerPage,
    DndSharePage,
    SharePage,
} from './pages';
import { PrivateRoute } from './routing';

const transformLocation = (inputLocation: Location) => {
    logger.info(
        `transformLocation called with location.Search ==
        ${inputLocation.search};
        location = ${inputLocation}`,
        'Init',
    );
    const queryStringParsed = queryString.parse(inputLocation.search);
    logger.info(`queryStringParsed.page == ${queryStringParsed.page}`, 'Init');
    logger.info(`inputLocation.hash == ${inputLocation.hash}`, 'Init');
    logger.info(
        // eslint-disable-next-line max-len
        `UserStorageHelper.storeHasUser() == ${UserStorageHelper.storeHasUser()}`,
        'Init',
    );

    if (inputLocation.hash == '#/login') {
        return {
            ...inputLocation,
            pathname: '/login',
        };
    }

    if (inputLocation.hash == '#/settings') {
        return {
            ...inputLocation,
            pathname: '/settings',
        };
    }

    if (inputLocation.hash == '#/settings/license') {
        return {
            ...inputLocation,
            pathname: '/settings/license',
        };
    }

    if (inputLocation.hash == '#/settings/general') {
        return {
            ...inputLocation,
            pathname: '/settings/general',
        };
    }

    if (inputLocation.hash == '#/share') {
        return {
            ...inputLocation,
            pathname: '/share',
        };
    }

    if (inputLocation.hash === '#/explorer') {
        return {
            ...inputLocation,
            pathname: '/explorer',
        };
    }

    return {
        ...inputLocation,
        pathname:
            '/' +
            (queryStringParsed.page === '' ||
            queryStringParsed.page === null ||
            queryStringParsed.page === undefined
                ? 'main'
                : queryStringParsed.page),
    };
};

const LOGIN_PATH = '/login';

const WhatsNewOnStartup = () => {
    const { releases, hasUnseen, markSeen } = useReleaseNotes();
    const user = useAppSelector(selectUser);
    const loggedIn = useAppSelector((state) => state.authentication.loggedIn);
    const busy = useAppSelector(selectIsFetching);
    const { pathname } = useLocation();
    const inLoginFlow = pathname === LOGIN_PATH;
    const visible = hasUnseen && loggedIn && !!user && !busy && !inLoginFlow;

    return (
        <WhatsNewDialog
            releases={releases}
            hidden={!visible}
            onDismiss={markSeen}
        />
    );
};

type AppProps = {
    isOfficeInitialized?: boolean;
    location: Location;
};

const App = (props: AppProps) => {
    const dispatch = useDispatch();
    const nextcloudTheming = useAppSelector(
        (state) => state.authentication.nextcloudTheming,
    );
    const theme = useAppSelector((state) => state.authentication.theme);

    const tokens = useMemo(
        () => resolveFluentTokens(theme, nextcloudTheming),
        [theme, nextcloudTheming],
    );
    const neutral = useMemo(() => buildNeutralPalette(tokens), [tokens]);
    const colors = useAppSelector(resolveThemeColors, shallowEqual);

    useLayoutEffect(() => {
        loadTheme({
            palette: {
                themePrimary: tokens.primaryColor,
                themeDarkAlt: tokens.primaryDarkColor,
                themeDark: tokens.primaryDarkColor,
                themeDarker: tokens.primaryDarkColor,
                themeLight: tokens.primaryLightColor,
                themeLighter: tokens.primaryLightColor,
                themeLighterAlt: tokens.primaryLightColor,
                neutralPrimary: tokens.backgroundTextColor,
                neutralDark: tokens.backgroundTextColor,
                neutralSecondary: neutral.neutralSecondary,
                neutralTertiary: neutral.neutralTertiary,
                neutralTertiaryAlt: neutral.neutralTertiaryAlt,
                neutralQuaternary: neutral.neutralQuaternary,
                neutralQuaternaryAlt: neutral.neutralQuaternaryAlt,
                neutralLight: neutral.neutralLight,
                neutralLighter: neutral.neutralLighter,
                white: tokens.backgroundColor,
                black: tokens.backgroundTextColor,
            },
            semanticColors: {
                link: colors.icon,
                inputIcon: colors.icon,
                menuIcon: colors.icon,
            },
            defaultFontStyle: {
                fontFamily: BODY_FONT_STACK,
            },
        });
        const cssVars: Record<string, string> = {
            '--sdt-primary': tokens.primaryColor,
            '--sdt-primary-text': tokens.primaryTextColor,
            '--sdt-primary-hover': tokens.primaryHoverColor,
            '--sdt-primary-dark': tokens.primaryDarkColor,
            '--sdt-primary-light': tokens.primaryLightColor,
            '--sdt-background': tokens.backgroundColor,
            '--sdt-background-text': tokens.backgroundTextColor,
            '--sdt-element': tokens.elementColor,
            '--sdt-element-text': tokens.elementTextColor,
            '--sdt-element-dark': tokens.elementDarkColor,
        };
        Object.entries(cssVars).forEach(([k, v]) =>
            document.documentElement.style.setProperty(k, v),
        );
    }, [tokens, neutral, colors]);

    useEffect(() => {
        logger.info('Initialized the application mainpage', 'Init');
        logger.debug('props.location=' + props.location, 'Init');
        dispatch(uiActions.initializeAddinUi(props.location));
        dispatch(appActions.initializeAddin(props.location));
    }, []);

    const wrapperStyle: React.CSSProperties = {
        backgroundColor: tokens.backgroundColor,
    };

    return (
        <div className="wrapper" style={wrapperStyle}>
            <Router>
                <NotificationBar />
                <WhatsNewOnStartup />
                <div className="main">
                    <Route
                        render={() => (
                            <Switch
                                location={
                                    transformLocation(
                                        props.location,
                                    ) as unknown as HistoryLocation
                                }
                            >
                                <Route path="/login" component={LoginPage} />
                                <PrivateRoute
                                    path="/share"
                                    component={SharePage}
                                />
                                <PrivateRoute
                                    path="/dnd-share"
                                    component={DndSharePage}
                                />
                                <PrivateRoute
                                    path="/settings"
                                    component={SettingsLayout}
                                />
                                <PrivateRoute
                                    path="/explorer"
                                    component={NextcloudExplorerPage}
                                />
                                <Redirect from="*" to="/share" />
                            </Switch>
                        )}
                    />
                </div>
                <Footer />
                <Waiting />
            </Router>
        </div>
    );
};

export default App;
